import normalize from "json-api-normalizer";
import omit from "lodash/omit";

import values from "lodash/values";
import queryString from "query-string";

import { GET, PATCH, POST } from "../../util/apiHelpers";
import { storageAvailable } from "../../util/storageHelpers";

import { receiveCostCenters } from "../employers/costCenterActions";
import { fetchOrganizationJobCastDefaults } from "../employers/organizationActions";
import { receiveOrgJobCategories } from "../employers/orgJobCategoryActions";
import { receiveRequiredTempAgencyAgreements } from "../employers/requiredTempAgencyAgreementActions";
import { receiveTempPrerequisites } from "../employers/tempPrerequisiteActions";
import { receiveInsuranceRequirementAgreements } from "../recruiters/insuranceRequirementAgreementActions";

import { processFeatureFlags } from "./featureFlagActions";
import { receiveInsuranceRequirements } from "./insuranceRequirementActions";
import {
  clearCurrentOrganization,
  receiveCurrentOrganization,
} from "./organizationActions";

// callback action types//
export const RECEIVE_INDIVIDUALS = "RECEIVE_INDIVIDUALS";
export const FAILED_LOGIN = "FAILED_LOGIN";
export const RECEIVE_CURRENT_INDIVIDUAL = "RECEIVE_CURRENT_INDIVIDUAL";
export const UPDATE_CURRENT_INDIVIDUAL = "UPDATE_CURRENT_INDIVIDUAL";
export const SET_LOADING = "SET_LOADING";
export const SUCCESSFUL_LOGOUT = "SUCCESSFUL_LOGOUT";
export const RECEIVE_REDIRECT_URL = "RECEIVE_REDIRECT_URL";
export const RECEIVE_EMAIL_SETTINGS = "RECEIVE_EMAIL_SETTINGS";
export const RECEIVE_VIEWING_AS = "RECEIVE_VIEWING_AS";
export const MFA_REQUIRED = "MFA_REQUIRED";
export const CLEAR_MFA_PENDING = "CLEAR_MFA_PENDING";

// api actions//
export const agreeToTerms = () => (dispatch) => {
  PATCH("/api/v3/authentication/agree-to-stripe", {})
    .then((res) => dispatch(receiveCurrentIndividual(res.data)))
    .catch((error) => console.log(error));
};

export const login = (credentials) => (dispatch) => {
  dispatch(setLoading(true));
  return POST("/api/v3/authentication/login", credentials)
    .then(
      (res) => {
        if (res.data.mfa_required) {
          dispatch({
            type: MFA_REQUIRED,
            payload: {
              intermediate_session_token: res.data.intermediate_session_token,
              member_id: res.data.member_id,
              totp_registered: res.data.totp_registered,
            },
          });
          return { mfa_required: true, totp_registered: res.data.totp_registered };
        }
        return processIndividual(res.data, dispatch);
      },
      (res) => {
        dispatch(failedLogin());
        return Promise.reject(res);
      }
    )
    .then((result) => {
      if (result && result.mfa_required) return result;
      
      return GET("/api/v3/authentication/feature_flags")
        .then((res) => {
          processFeatureFlags(res.data, dispatch);
        })
        .catch((error) => {
          console.warn('Feature flags not available, using defaults:', error);
          // Return nothing or default value
          return;
        });
    });
};

export const logout =
  (data = { expired: false }) =>
  (dispatch) =>
    PATCH("/api/v3/authentication/logout", data)
      .then((res) => {
        storageAvailable() && localStorage.removeItem("authToken");
        // for gordon...
        storageAvailable() && localStorage.removeItem("authentication");
        dispatch(clearCurrentOrganization());
        dispatch(successfulLogout());
        dispatch(clearViewingAs());
      })
      .catch((error) => Promise.reject(error));

export const createPasswordReset = (data) =>
  POST("/api/v3/password_reset", data);

export const updatePassword = (data) => PATCH("/api/v3/password_reset", data);

export const verifyPasswordResetMfa = (data) => (dispatch) =>
  POST("/api/v3/password_reset/verify_mfa", data).then(() => dispatch(clearMfaPending()));

export const recoverPasswordResetMfa = (data) => (dispatch) =>
  POST("/api/v3/password_reset/recover_mfa", data).then(() => dispatch(clearMfaPending()));

export const fetchRedirectURL = () => (dispatch) =>
  GET("/api/v3/public/redirects")
    .then((res) => {
      dispatch(receiveRedirectURL(res.data.redirectURL));
    })
    .catch((error) => Promise.reject(error));

export const updateProfile = (formData) => (dispatch) =>
  PATCH("/api/v3/individual", formData)
    .then((res) => {
      if (res.data.redirect_to_login) {
        storageAvailable() && localStorage.removeItem("authToken");
        storageAvailable() && localStorage.removeItem("ghosting");
        window.location.href = "/app/login";
        return;
      }
      const normalized = normalize(res.data);
      dispatch(receiveIndividuals(normalized.individual));
    })
    .catch((error) => Promise.reject(error.response.data));

export const deactivateCurrentAccount = () => (dispatch) =>
  POST("/api/v3/individual/deactivate").then(() => {
    storageAvailable() && localStorage.removeItem("authToken");
    dispatch(clearCurrentOrganization());
    dispatch(successfulLogout());
    dispatch(clearViewingAs());
  });

// callback actions//
export const failedLogin = () => ({ type: FAILED_LOGIN });
export const successfulLogout = () => ({ type: SUCCESSFUL_LOGOUT });

const processFullEmployer = (userData, dispatch) => {
  dispatch(receiveCurrentIndividual(userData.individual));
  dispatch(receiveCurrentOrganization(userData.organization));
  dispatch(receiveCostCenters(userData.costCenter));
  dispatch(
    receiveRequiredTempAgencyAgreements(userData.requiredTempAgencyAgreement)
  );
  dispatch(receiveTempPrerequisites(userData.tempPrerequisite));
  dispatch(receiveOrgJobCategories(userData.orgJobCategories));
  dispatch(receiveInsuranceRequirements(userData.insuranceRequirements));
  return dispatch(fetchOrganizationJobCastDefaults());
};

const processFullRecruiter = (userData, dispatch) => {
  dispatch(receiveCurrentIndividual(userData.individual));
  dispatch(receiveCurrentOrganization(userData.organization));
  dispatch(
    receiveInsuranceRequirementAgreements(
      userData.insuranceRequirementAgreements
    )
  );
  dispatch(receiveInsuranceRequirements(userData.insuranceRequirements));
  dispatch(receiveTempPrerequisites(userData.tempPrerequisite));

  return userData.individual;
};

const BETA_MERIDIAN_REDIRECT_KEY = "meridianRedirectAt";
const MAX_MERIDIAN_ATTEMPTS = 2;

// Beta employers live on Meridian. The URL attempt counter (round-tripped through
// meridian's /auth/handoff bounce) breaks redirect loops deterministically; the 15s
// timestamp guard remains as backup for flows that don't carry the counter.
const redirectBetaUserToMeridian = async (data) => {
  const url = data?.data?.attributes?.meridian_redirect_url;
  if (!url) return false; // non-beta users never get here

  const { meridian_attempt: rawAttempt, return_to: queryReturnTo } = queryString.parse(
    window.location.search
  );
  const priorAttempts = Number(rawAttempt) || 0;

  if (priorAttempts >= MAX_MERIDIAN_ATTEMPTS) {
    window.bugsnagClient?.notify(
      new Error(`Meridian handoff failed ${priorAttempts} times; staying on Bran`)
    );
    try {
      window.sessionStorage.removeItem(BETA_MERIDIAN_REDIRECT_KEY);
    } catch {
      // noop
    }
    return false;
  }

  // Backup time guard, only where meridian's bounces land (/app/login) and only when
  // no counter is present (a stale meridian deploy bounces without one — the counter
  // supersedes it otherwise, since a handoff bounce round-trips in well under 15s).
  // Deliberate visits to any other Bran path always redirect: beta users live on Meridian.
  if (priorAttempts === 0 && window.location.pathname.includes("/login")) {
    try {
      const lastAttempt = Number(window.sessionStorage.getItem(BETA_MERIDIAN_REDIRECT_KEY)) || 0;
      if (Date.now() - lastAttempt < 15000) {
        window.sessionStorage.removeItem(BETA_MERIDIAN_REDIRECT_KEY);
        return false;
      }
    } catch {
      // no sessionStorage: proceed without the loop guard
    }
  }
  try {
    window.sessionStorage.setItem(BETA_MERIDIAN_REDIRECT_KEY, String(Date.now()));
  } catch {
    // noop
  }

  // One-time-code handoff: meridian exchanges the code server-side and sets the
  // Rails session first-party on its origin, so login works even in browsers that
  // won't present the shared .recruitifi.com cookie there. Prefer the URL's own
  // return_to (a meridian bounce's deep link) over the serialized value; the
  // server re-validates it against the meridian origin either way.
  try {
    const res = await POST("/api/v3/authentication/meridian_handoff", {
      return_to: queryReturnTo || url,
    });
    const handoffUrl = res?.data?.url;
    if (handoffUrl) {
      const sep = handoffUrl.includes("?") ? "&" : "?";
      window.location.assign(`${handoffUrl}${sep}attempt=${priorAttempts + 1}`);
      return true;
    }
  } catch {
    // fall through to the plain redirect (pre-handoff behavior)
  }

  window.location.assign(url);
  return true;
};

export const processIndividual = async (data, dispatch) => {
  // Persist the token BEFORE any Meridian redirect: if the handoff bounces the user
  // back, bootstrap (useAppInitialization) must still recognize them, and the mint
  // POST above needs the bearer header (apiHelpers.generateHeader).
  const token = data?.data?.attributes?.token;
  if (token) localStorage.setItem("authToken", token);

  if (await redirectBetaUserToMeridian(data)) return;

  const normalizedData = normalize(data);
  const insReq = {};
  Object.values(
    normalizedData.organization
  )[0].attributes.activeInsuranceRequirements.data.forEach((a) => {
    insReq[a.id] = a.attributes;
  });

  const individualData = {
    individual: normalizedData.individual,
    organization: normalizedData.organization,
    costCenter: normalizedData.costCenter,
    requiredTempAgencyAgreement: normalizedData.requiredTempAgencyAgreement,
    tempPrerequisite: normalizedData.tempPrerequisite,
    insuranceRequirements: insReq,
    insuranceRequirementAgreements:
      normalizedData.insuranceRequirementAgreement,
    orgJobCategories: normalizedData.orgJobCategory,
  };

  const individual = values(individualData.individual)[0].attributes;

  if (individual.accountManager) {
    return processFullRecruiter(individualData, dispatch);
  }
  if (individual.employer) {
    return processFullEmployer(individualData, dispatch);
  }
  if (individual.recruiter) {
    return processFullRecruiter(individualData, dispatch);
  }
};

export const receiveCurrentIndividual = (individual) => ({
  type: RECEIVE_CURRENT_INDIVIDUAL,
  payload: omit(individual, ["organization", "password"]),
});

export const updateCurrentIndividual = (updateAttributes) => ({
  type: UPDATE_CURRENT_INDIVIDUAL,
  payload: updateAttributes,
});

export const setLoading = (bool) => ({
  type: SET_LOADING,
  payload: bool,
});

export const receiveRedirectURL = (url) => ({
  type: RECEIVE_REDIRECT_URL,
  payload: url,
});

export const receiveIndividuals = (payload) => ({
  type: RECEIVE_INDIVIDUALS,
  payload,
});

export const receiveEmailSettings = (settings) => ({
  type: RECEIVE_EMAIL_SETTINGS,
  payload: settings,
});

export const receiveViewingAs = (payload) => ({
  type: RECEIVE_VIEWING_AS,
  payload,
});

export const clearViewingAs = () => (dispatch) => {
  if (storageAvailable()) {
    localStorage.removeItem("viewingAs");
    localStorage.removeItem("oldViewingAs");
  }

  return dispatch(receiveViewingAs(null));
};

export const createTotpLogin = (data) => () =>
  POST("/api/v3/mfa/totp/create_login", data).then((res) => res.data);

// Mirrors SignInForm's query merge: keeps a meridian deep link (return_to) alive
// through the MFA step so the post-verify redirect can honor it.
const withReturnTo = (data) => {
  const { return_to: returnTo } = queryString.parse(window.location.search);
  return returnTo ? { return_to: returnTo, ...data } : data;
};

export const verifyTotpLogin = (data) => (dispatch) =>
  POST("/api/v3/mfa/totp/verify_login", withReturnTo(data))
    .then((res) => processIndividual(res.data, dispatch))
    .then(() => {
      dispatch(clearMfaPending());
      return GET("/api/v3/authentication/feature_flags").then((res) => {
        processFeatureFlags(res.data, dispatch);
      });
    });

export const verifyTotpLoginDeferred = (data) => () =>
  POST("/api/v3/mfa/totp/verify_login", withReturnTo(data)).then((res) => res.data);

export const completeDeferredLogin = (data) => (dispatch) =>
  processIndividual(data, dispatch).then(() => {
    dispatch(clearMfaPending());
    return GET("/api/v3/authentication/feature_flags").then((res) => {
      processFeatureFlags(res.data, dispatch);
    });
  });

export const recoverTotpLogin = (data) => (dispatch) =>
  POST("/api/v3/mfa/totp/recover_login", withReturnTo(data))
    .then((res) => processIndividual(res.data, dispatch))
    .then(() => {
      dispatch(clearMfaPending());
      return GET("/api/v3/authentication/feature_flags").then((res) => {
        processFeatureFlags(res.data, dispatch);
      });
    });

export const fetchMfaStatus = () => () =>
  GET("/api/v3/mfa/status").then((res) => res.data);

export const createTotpSettings = () => () =>
  POST("/api/v3/mfa/totp/create").then((res) => res.data);

export const verifyTotpSettings = (code) => () =>
  POST("/api/v3/mfa/totp/verify", { code }).then((res) => res.data);

export const clearMfaPending = () => ({ type: CLEAR_MFA_PENDING });
